import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import Header from "../../components/header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";

import { useSeo } from "../../hooks/useSeo";

const staticMeta = {
  title: "Our Portfolio | RashTech Info Projects",
  description:
    "Explore RashTech Info’s portfolio of web, mobile, and custom software projects across industries. See what we’ve delivered for clients.",
  url: "https://rashtechinfo.com/portfolio",
  image: "https://rashtechinfo.com/og-image.png", // replace with your actual asset if needed
  siteName: "RashTech Info",
  keywords:
    "portfolio, case studies, projects, web development, mobile apps, software, RashTech Info",
  themeColor: "#0A0D1C",
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: staticMeta.siteName,
  url: "https://rashtechinfo.com/",
  logo: "https://rashtechinfo.com/logo.png",
  sameAs: [
    "https://www.facebook.com/rashtechinfo",
    "https://www.linkedin.com/company/rashtechinfo",
    "https://twitter.com/rashtechinfo",
  ],
};

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  // Load SEO for this route
  const { seo } = useSeo("/getSeo");

  // Merge dynamic SEO over static defaults
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };
    return {
      title: dynamic.title || staticMeta.title,
      description: dynamic.description || staticMeta.description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url: dynamic.canonicalUrl || staticMeta.url,
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo]);

  useEffect(() => {
    const fetchProjectsAndCategories = async () => {
      try {
        const [projectsRes, categoriesRes] = await Promise.all([
          fetch(`https://api.rashtechinfo.com/getAllProjects`),
          fetch(`https://api.rashtechinfo.com/getAllProjectCategories`),
        ]);
        if (!projectsRes.ok || !categoriesRes.ok) {
          throw new Error("Failed to fetch data");
        }
        const projectsData = await projectsRes.json();
        const categoriesData = await categoriesRes.json();

        setPortfolio(projectsData.data || []);
        setCategories(categoriesData.categories || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchProjectsAndCategories();
  }, []);

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c._id === categoryId);
    return cat ? cat.categoryName : categoryId;
  };

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const filteredProjects =
    activeFilter === "all"
      ? portfolio.slice(0, 18)
      : portfolio
          .slice(0, 18)
          .filter(
            (project) =>
              getCategoryName(project.projectCategory).toLowerCase() ===
              activeFilter
          );

  return (
    <Fragment>
      <Helmet>
        {/* Primary */}
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && (
          <meta name="keywords" content={computed.keywords} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && (
          <meta name="twitter:image" content={computed.image} />
        )}

        {/* Optional preconnects */}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />

        {/* JSON-LD (Organization) */}
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

        {/* JSON-LD (Breadcrumbs for Portfolio) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://rashtechinfo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Portfolio",
                item: computed.url,
              },
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle pageTitle={"Our Portfolio"} pagesub={"Portfolio"} pageTop={"Our"} />

        <section className="portfolio_section section_space bg-light">
          <div className="container">
            <div className="filter_elements_nav">
              <ul className="unordered_list justify-content-center">
                <li
                  className={activeFilter === "all" ? "active" : ""}
                  onClick={() => handleFilterClick("all")}
                >
                  See All
                </li>
                {categories.map((cat) => (
                  <li
                    key={cat._id}
                    className={
                      activeFilter === cat.categoryName.toLowerCase()
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      handleFilterClick(cat.categoryName.toLowerCase())
                    }
                  >
                    {cat.categoryName}
                  </li>
                ))}
              </ul>
            </div>

            <div className="filter_elements_wrapper row">
              {filteredProjects.map((project, prj) => (
                <div className="col-lg-6" key={prj}>
                  <div className="portfolio_block portfolio_layout_2">
                    <div className="portfolio_image">
                      <Link
                        onClick={ClickHandler}
                        className="portfolio_image_wrap bg-light"
                        to={`/portfolio/${project.Project_title.replace(/\s+/g, "-")}`}
                      >
                        <img
                          src={`https://api.rashtechinfo.com/uploads/${project.projectImage}`}
                          alt={project.Project_title || "Portfolio Project"}
                          style={{
                            width: "100%",
                            contain: "cover",
                            height: "100%",
                          }}
                        />
                      </Link>
                    </div>
                    <div className="portfolio_content">
                      <h3 className="portfolio_title">
                        <Link
                          onClick={ClickHandler}
                          to={`/portfolio/${project.Project_title.replace(/\s+/g, "-")}`}
                        >
                          {project.Project_title}
                        </Link>
                      </h3>
                      <ul className="category_list unordered_list">
                        <li>
                          <Link
                            onClick={ClickHandler}
                            to={`/portfolio/${project.Project_title.replace(/\s+/g, "-")}`}
                          >
                            <i className="fa-solid fa-tags"></i>{" "}
                            {getCategoryName(project.projectCategory)}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
              {filteredProjects.length === 0 && (
                <div className="col-12 text-center py-4">
                  No projects found for the selected category.
                </div>
              )}
            </div>
          </div>
        </section>

        <CtaSection />
      </main>
      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default PortfolioPage;
